import React, { useState, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Popper, MenuItem, Box, Typography, Divider, Collapse, Paper, Fade, ClickAwayListener } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import './KebabMenu.scss';

/**
 * Custom hook for menu state management
 */
export const useMenuState = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const nodeRef = React.useRef(null);
  const rectRef = React.useRef(null);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMenuId(null);
    nodeRef.current = null;
    rectRef.current = null;
  }, []);

  const handleOpen = useCallback((event, id = null) => {
    event.stopPropagation();
    const node = event.currentTarget;
    nodeRef.current = node;
    rectRef.current = node.getBoundingClientRect();

    // Virtual anchor: rect is refreshed on scroll (so horizontal scroll works)
    // but is NOT recalculated during MUI scroll-lock body shifts
    const virtualAnchor = {
      getBoundingClientRect: () => {
        let activeNode = nodeRef.current || node;
        if (activeNode && !document.contains(activeNode) && id) {
          const newEl = document.querySelector(`[data-menu-id="${id}"]`);
          if (newEl) {
            nodeRef.current = newEl;
            activeNode = newEl;
          }
        }
        // Only refresh rect if the node is currently attached to the DOM.
        // This prevents the menu from snapping to the top-left (0, 0) during page/list loading
        // when the original anchor element is temporarily unmounted.
        if (activeNode && document.contains(activeNode)) {
          rectRef.current = activeNode.getBoundingClientRect();
        }
        return rectRef.current ?? node.getBoundingClientRect();
      },
      get contextElement() {
        let activeNode = nodeRef.current || node;
        if (activeNode && !document.contains(activeNode) && id) {
          const newEl = document.querySelector(`[data-menu-id="${id}"]`);
          if (newEl) {
            nodeRef.current = newEl;
            activeNode = newEl;
          }
        }
        return activeNode;
      }
    };

    setAnchorEl(virtualAnchor);
    setMenuId(id);
  }, []);

  // Refresh the frozen rect on scroll so menu tracks horizontal scrolling
  React.useEffect(() => {
    if (!anchorEl) return;
    const onScroll = () => {
      let activeNode = nodeRef.current;
      if (activeNode && !document.contains(activeNode) && menuId) {
        const newEl = document.querySelector(`[data-menu-id="${menuId}"]`);
        if (newEl) {
          nodeRef.current = newEl;
          activeNode = newEl;
        }
      }
      if (activeNode && document.contains(activeNode)) {
        rectRef.current = activeNode.getBoundingClientRect();
      }
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, [anchorEl, menuId]);

  const isOpen = Boolean(anchorEl);

  return {
    anchorEl,
    menuId,
    isOpen,
    handleOpen,
    handleClose
  };
};

/**
 * Generic menu item click handler
 */
export const createMenuItemHandler = (onItemClick, onClose) => {
  return (item) => {
    onItemClick?.(item);
    onClose();
  };
};

const KebabMenu = ({ 
  anchorEl, 
  open, 
  onClose, 
  menuItems = [], 
  onItemClick,
  disabled = false,
  disableClickAway = false,
  scrollWithAnchor = false
}) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState({});
  const scrollContainer = scrollWithAnchor
    ? (anchorEl?.contextElement?.closest?.('.content-area')
      || anchorEl?.closest?.('.content-area'))
    : null;

  // Reset expansion state when menu closes
  React.useEffect(() => {
    if (!open) {
      setExpandedItems({});
    }
  }, [open]);

  const toggleExpand = (e, itemId) => {
    if (e) e.stopPropagation();
    setExpandedItems(prev => ({ 
      ...prev, 
      [itemId]: true // Once expanded, stay expanded
    }));
  };

  const getColorClass = (color, index) => {
    const colors = ['blue', 'red', 'purple'];
    return color || colors[index % colors.length];
  };

  const handleItemClick = (item) => {
    onItemClick?.(item);
    if (!item.stayOpen) {
      onClose();
    }
  };

  const menuContent = (
    <Paper className="kebab-menu-paper" sx={{ mt: 0.5, overflow: 'hidden', py: 0.5 }}>
      {menuItems.map((item, index) => {
        if (item.type === 'divider') {
          return <Divider key={`divider-${index}`} sx={{ my: 0.3 }} />;
        }

        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems[item.id];
        const isSelected = item.isSelected;

        return (
          <Fragment key={item.id}>
            <MenuItem
              disableRipple
              onClick={(e) => {
                if (hasChildren) {
                  toggleExpand(e, item.id);
                  onItemClick?.(item);
                } else {
                  handleItemClick(item);
                }
              }}
              disabled={disabled || item.disabled}
              className={`kebab-menu-item ${getColorClass(item.color, index)} ${disabled || item.disabled ? 'disabled' : ''} ${isExpanded ? 'active-header' : ''} ${isSelected ? 'active-selected' : ''}`}
              sx={{
                ...item.sx,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                position: 'relative',
                py: 0.7,
                mb: isExpanded ? 0.2 : 0,
                borderBottom: isExpanded ? `1px solid ${COLORS.ui.lightDivider}` : 'none',
                bgcolor: isExpanded ? COLORS.white : 'inherit',
                '&:hover': {
                  bgcolor: isExpanded ? COLORS.white : undefined
                }
              }}
            >
              <Typography sx={{ fontWeight: 500, fontSize: '15px', textAlign: 'left', color: COLORS.darkGray, pl: 0.5, flex: 1 }}>
                {item.labelNode || t(item.label)}
              </Typography>
              {hasChildren && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  {isExpanded ?
                    <KeyboardArrowUp sx={{ fontSize: 16, color: COLORS.pureBlack }} /> :
                    <KeyboardArrowDown sx={{ fontSize: 16, color: COLORS.pureBlack }} />
                  }
                </Box>
              )}
            </MenuItem>

            {hasChildren && (
              <Collapse in={isExpanded} timeout="auto">
                <Box className="kebab-menu-children">
                  {item.children.map((child, cIndex) => (
                    <Fragment key={child.id || `child-${cIndex}`}>
                      <MenuItem
                        disableRipple
                        onClick={() => handleItemClick(child)}
                        disabled={disabled || child.disabled}
                        className={`kebab-menu-item ${getColorClass(child.color, cIndex)} ${disabled || child.disabled ? 'disabled' : ''}`}
                        sx={{
                          py: 0.5,
                          minHeight: '26px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            bgcolor: COLORS.ui.menuHover
                          },
                          ...child.sx
                        }}
                      >
                        <Typography sx={{
                          fontSize: '14.5px',
                          fontWeight: 400,
                          color: child.sx?.color || (child.disabled ? COLORS.lightGray : COLORS.mediumGray)
                        }}>
                          {t(child.label)}
                        </Typography>
                      </MenuItem>
                      {cIndex < item.children.length - 1 && <Divider sx={{ mx: 1, opacity: 0.3, my: 0 }} />}
                    </Fragment>
                  ))}
                </Box>
              </Collapse>
            )}
          </Fragment>
        );
      })}
    </Paper>
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      strategy={scrollContainer ? 'absolute' : 'fixed'}
      container={scrollContainer || undefined}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8]
          }
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
            altAxis: true
          }
        },
        {
          name: 'computeStyles',
          options: {
            adaptive: false
          }
        }
      ]}
      transition
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Box>
            {disableClickAway ? menuContent : (
              <ClickAwayListener onClickAway={onClose}>
                {menuContent}
              </ClickAwayListener>
            )}
          </Box>
        </Fade>
      )}
    </Popper>
  );
};

export default KebabMenu;
